import { useCallback, RefObject } from "react";
import { CellValueChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { PRD1010_R01Dto } from "../Dto_PRD1010_R01";
import { createSearchFunctionFromCategory } from "src/utils/categoryUtils";
import { focusToCell } from "src/utils/grid/GridHandlerContent";

interface UsePRD1010_R01CellChangeProps {
	updatePRD1010_R01List: (index: number, data: PRD1010_R01Dto) => void;
	gridRef: RefObject<AgGridReact<PRD1010_R01Dto> | null>;
	settingCodeSearchModalProps: (
		category: string,
		search: string,
		isInfo: boolean,
		handleFocusId: string,
		originalData: PRD1010_R01Dto,
		valueIndex: number,
	) => void;
}

export function usePRD1010_R01CellChange({
	updatePRD1010_R01List,
	gridRef,
	settingCodeSearchModalProps,
}: UsePRD1010_R01CellChangeProps) {
	const handleItemCdSearch = useCallback(
		async (newValue: string, rowData: PRD1010_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updatePRD1010_R01List(rowData.valueIndex!, {
					...rowData,
					itemCd: "",
					itemNm: "",
					itemSpec: "",
					itemUnit: "",
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("item");
			if (!searchFn) {
				settingCodeSearchModalProps("품목", newValue, false, "itemCd", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(newValue.trim().toUpperCase(), (item: any) => {
					updatePRD1010_R01List(rowData.valueIndex!, {
						...rowData,
						itemCd: item.itemCd ?? "",
						itemNm: item.itemNm ?? "",
						itemSpec: item.itemSpec ?? "",
						itemUnit: item.itemUnit ?? "",
						isModified: rowData.inserted ? false : true,
					});
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							focusToCell(gridRef, rowData.valueIndex!, "orderQty");
						});
					});
				});

				if (!found) {
					settingCodeSearchModalProps("품목", newValue, false, "itemCd", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("품목", newValue, false, "itemCd", rowData, rowData.valueIndex!);
			}
		},
		[updatePRD1010_R01List, settingCodeSearchModalProps, gridRef],
	);

	const handleCellChange = useCallback(
		async (event: CellValueChangedEvent<PRD1010_R01Dto>) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof PRD1010_R01Dto;

			if (field === "itemCd" && newValue !== oldValue) {
				updatePRD1010_R01List(data.valueIndex!, {
					...data,
					[field]: newValue,
					isModified: data.inserted ? false : true,
				});
				await handleItemCdSearch(newValue, data);
				return;
			}

			updatePRD1010_R01List(data.valueIndex!, {
				...data,
				[field]: newValue,
				isModified: true,
			});
		},
		[handleItemCdSearch, updatePRD1010_R01List]
	);

	return {
		handleCellChange,
	};
}
